<template>
  <div class="ma-card" :class="{ 'ma-single': isNarrow }">
    <div class="ma-header">
      <div class="ma-title">{{ title }}</div>
      <div class="ma-subtitle" v-if="subtitle">{{ subtitle }}</div>
    </div>
    <div class="ma-grid">
      <div v-for="(chart, idx) in parsedCharts" :key="idx" class="ma-chart-item">
        <div class="ma-chart-title">{{ chart.title }}</div>
        <div class="ma-chart-metrics" v-if="chart.metrics && chart.metrics.length">
          <div v-for="(m, mi) in chart.metrics" :key="mi" class="ma-metric">
            <span class="ma-metric-label">{{ m.label }}</span>
            <span class="ma-metric-value" :class="{ highlight: m.highlight }">{{ m.value }}</span>
          </div>
        </div>
        <div ref="chartRefs" class="ma-chart-container"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'MetricAnalysis',
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    charts: { type: [Array, String], default: () => [] }
  },
  data() {
    return {
      isNarrow: false,
      chartInstances: []
    }
  },
  computed: {
    parsedCharts() {
      if (Array.isArray(this.charts)) return this.charts
      if (typeof this.charts === 'string') {
        try {
          const parsed = JSON.parse(this.charts)
          if (Array.isArray(parsed)) return parsed
        } catch (e) { /* ignore */ }
      }
      return []
    }
  },
  mounted() {
    this.checkWidth()
    window.addEventListener('resize', this.checkWidth)
    this.$nextTick(() => {
      this.initCharts()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkWidth)
    this.destroyCharts()
  },
  methods: {
    checkWidth() {
      const el = this.$el
      if (el) {
        const width = el.offsetWidth
        this.isNarrow = width < 500
        this.$nextTick(() => {
          this.resizeCharts()
        })
      }
    },
    initCharts() {
      this.destroyCharts()
      this.$nextTick(() => {
        const refs = this.$refs.chartRefs
        if (!refs) return
        const charts = this.parsedCharts
        refs.forEach((ref, idx) => {
          if (!ref || !charts[idx]) return
          const instance = echarts.init(ref)
          const option = this.buildChartOption(charts[idx])
          instance.setOption(option)
          this.chartInstances.push(instance)
        })
      })
    },
    destroyCharts() {
      this.chartInstances.forEach(inst => inst && inst.dispose())
      this.chartInstances = []
    },
    resizeCharts() {
      this.chartInstances.forEach(inst => inst && inst.resize())
    },
    buildChartOption(chart) {
      const series = (chart.series || []).map(s => ({
        name: s.name,
        type: 'line',
        data: s.data,
        smooth: false,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2 },
        itemStyle: { color: s.color || '#4e8cff' }
      }))

      const colors = ['#4e8cff', '#f59e0b', '#22c55e', '#ef4444']
      series.forEach((s, i) => {
        if (!s.itemStyle.color) {
          s.itemStyle.color = colors[i % colors.length]
        }
      })

      return {
        tooltip: {
          trigger: 'axis',
          textStyle: { fontSize: 11 }
        },
        legend: {
          bottom: 0,
          left: 'center',
          itemWidth: 12,
          itemHeight: 8,
          textStyle: { fontSize: 11, color: '#6b7280' },
          data: (chart.series || []).map(s => s.name)
        },
        grid: {
          left: '3%',
          right: '4%',
          top: 10,
          bottom: 36,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: chart.xAxis || [],
          boundaryGap: false,
          axisLine: { lineStyle: { color: '#e5e7eb' }},
          axisLabel: { fontSize: 11, color: '#6b7280' },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { fontSize: 11, color: '#6b7280' },
          splitLine: { lineStyle: { color: '#f3f4f6' }}
        },
        series
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ma-card {
  padding: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.ma-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.ma-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.ma-subtitle {
  font-size: 12px;
  color: #6b7280;
}

.ma-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.ma-single .ma-grid {
  grid-template-columns: 1fr;
}

.ma-chart-item {
  background: #fafbfc;
  border: 1px solid #f0f1f3;
  border-radius: 8px;
  padding: 12px 14px;
}

.ma-chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 6px;
}

.ma-chart-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 8px;
}

.ma-metric {
  font-size: 12px;
  color: #6b7280;
}

.ma-metric-label {
  margin-right: 4px;
}

.ma-metric-value {
  color: #1f2937;
  font-weight: 500;

  &.highlight {
    color: #ef4444;
  }
}

.ma-chart-container {
  width: 100%;
  height: 180px;
}

.ma-single .ma-chart-container {
  height: 200px;
}
</style>
